type LaunchModalSuccessResponse<P> = {
  success: true,
  result: P,
}

type LaunchModalErrorResponse = {
  success: false,
  errors: string[],
}

// P is the generic type for the payload (defaults to Record<string, any> if not specified)
export type LaunchModalParams<P extends Record<string, any> = Record<string, any>> = {
  // Use a Record type that links the string key to the specific function signature.
  // The functions must accept the generic payload P.
  callbacks: Record<string, (payload: P) => void>,
  modalKey: string,
  payload?: P,
}

/**
 * Launches a modal by calling a dynamically named function from the callbacks object.
 * @template P The specific type of the payload object.
 * @param params The parameters including callbacks, modalKey, and the payload.
 * @returns A result object indicating success/failure and the payload.
 */
const launchModal = <P extends Record<string, any> = Record<string, any>>(
  params: LaunchModalParams<P>,
): LaunchModalSuccessResponse<P> | LaunchModalErrorResponse => {
  const { callbacks, modalKey, payload = {} as P } = params

  const showModalFnName = `show${modalKey}`

  const showModalFn = callbacks[showModalFnName]

  if (showModalFn){
    showModalFn(payload)

    return {
      success: true,
      result: payload,
    }
  }

  const errorMessage = `${showModalFnName} not defined in PageContext callbacks`
  console.error(errorMessage)

  return {
    success: false,
    errors: [errorMessage],
  }
}

export default launchModal
